import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Inbox, Mail, Eye, RefreshCw, Check, X, AlertCircle, 
  ExternalLink, Loader2, ChevronLeft, Car
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

// Channel colors and labels
const CHANNEL_CONFIG = {
  PARKOS: { color: 'bg-blue-500', label: 'Parkos' },
  PARKVIA: { color: 'bg-green-500', label: 'Parkvia' },
  ONEPARK: { color: 'bg-purple-500', label: 'Onepark' },
  LOOKING4PARKING: { color: 'bg-orange-500', label: 'Looking4' },
  FREE2MOVE: { color: 'bg-pink-500', label: 'Free2Move' },
  UNKNOWN: { color: 'bg-gray-500', label: 'Desconocido' },
  DIRECT: { color: 'bg-[#FFD700]', label: 'Directo' },
};

const STATUS_CONFIG = {
  RECEIVED: { color: 'bg-gray-500', label: 'Recibido' },
  PARSED: { color: 'bg-green-500', label: 'Procesado' },
  NEEDS_REVIEW: { color: 'bg-yellow-500', label: 'Revisar' },
  ERROR: { color: 'bg-red-500', label: 'Error' },
};

export const ChannelBadge = ({ channel }) => {
  const config = CHANNEL_CONFIG[channel] || CHANNEL_CONFIG.UNKNOWN;
  return (
    <Badge className={`${config.color} text-white text-xs`}>
      {config.label}
    </Badge>
  );
};

const AdminInbox = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [editedData, setEditedData] = useState(null);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (channelFilter && channelFilter !== 'all') params.append('channel', channelFilter);
      
      const response = await api.get(`/integrations/inbound-emails?${params}`);
      setEmails(response.data.emails);
    } catch (error) {
      toast.error('Error al cargar emails');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, channelFilter]);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/integrations/channel-stats');
      setStats(response.data);
    } catch (error) {
      console.debug('Failed to load channel stats:', error.message);
    }
  }, []);

  useEffect(() => {
    loadEmails();
    loadStats();
  }, [loadEmails, loadStats]);

  const openDetail = async (email) => {
    try {
      const response = await api.get(`/integrations/inbound-emails/${email.id}`);
      setSelectedEmail(response.data);
      setEditedData(response.data.parsed_data || {});
      setShowDetail(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  const retryParse = async (emailId) => {
    try {
      const response = await api.post(`/integrations/inbound-emails/${emailId}/retry`);
      if (response.data.success) {
        toast.success('Email reprocesado');
        loadEmails();
        if (selectedEmail?.id === emailId) {
          setSelectedEmail(prev => ({...prev, ...response.data}));
          setEditedData(response.data.parsed_data);
        }
      }
    } catch (error) {
      toast.error('Error al reprocesar');
    }
  };

  const createReservation = async () => {
    if (!selectedEmail || !editedData) return;
    try {
      const response = await api.post(
        `/integrations/inbound-emails/${selectedEmail.id}/create-reservation`,
        editedData
      );
      if (response.data.success) {
        toast.success('Reserva creada: ' + response.data.reservation_id);
        loadEmails();
        setShowDetail(false);
      }
    } catch (error) {
      toast.error('Error al crear reserva');
    }
  };

  const ignoreEmail = async () => {
    if (!selectedEmail) return;
    try {
      await api.post(`/integrations/inbound-emails/${selectedEmail.id}/ignore`);
      toast.success('Email ignorado');
      loadEmails();
      setShowDetail(false);
    } catch (error) {
      toast.error('Error al ignorar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Inbox className="w-6 h-6 text-[#FFD700]" />
          <h2 className="text-xl font-bold">Inbox de Canales</h2>
        </div>
        <Button onClick={loadEmails} className="btn-secondary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Channel Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {stats.by_channel?.map(ch => (
            <div key={ch._id || 'unknown'} className="card-dark p-4">
              <ChannelBadge channel={ch._id || 'DIRECT'} />
              <p className="text-2xl font-bold mt-2">{ch.count}</p>
              <p className="text-xs text-zinc-400">{((ch.revenue || 0) / 100).toFixed(0)}€</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="RECEIVED">Recibido</SelectItem>
            <SelectItem value="PARSED">Procesado</SelectItem>
            <SelectItem value="NEEDS_REVIEW">Revisar</SelectItem>
            <SelectItem value="ERROR">Error</SelectItem>
          </SelectContent>
        </Select>

        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10">
            <SelectValue placeholder="Canal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="PARKOS">Parkos</SelectItem>
            <SelectItem value="PARKVIA">Parkvia</SelectItem>
            <SelectItem value="ONEPARK">Onepark</SelectItem>
            <SelectItem value="LOOKING4PARKING">Looking4Parking</SelectItem>
            <SelectItem value="FREE2MOVE">Free2Move</SelectItem>
            <SelectItem value="UNKNOWN">Desconocido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Email List */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 text-[#FFD700] animate-spin" />
        </div>
      ) : emails.length === 0 ? (
        <div className="text-center py-10 text-zinc-400">
          <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No hay emails</p>
        </div>
      ) : (
        <div className="space-y-2">
          {emails.map(email => (
            <motion.div
              key={email.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-dark p-4 hover:border-white/20 cursor-pointer transition-colors"
              onClick={() => openDetail(email)}
              data-testid={`email-row-${email.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Mail className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{email.subject || '(Sin asunto)'}</p>
                    <p className="text-sm text-zinc-400 truncate">{email.from_address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ChannelBadge channel={email.detected_channel} />
                  <Badge className={`${STATUS_CONFIG[email.parse_status]?.color} text-white text-xs`}>
                    {STATUS_CONFIG[email.parse_status]?.label}
                  </Badge>
                  {email.linked_reservation_id && (
                    <Badge className="bg-green-500/20 text-green-400 text-xs">
                      <Car className="w-3 h-3 mr-1" />
                      Vinculado
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-zinc-500 flex-shrink-0">
                  {formatDate(email.received_at)}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Detalle del Email
            </DialogTitle>
          </DialogHeader>

          {selectedEmail && (
            <div className="space-y-6">
              {/* Email Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-400">De</p>
                  <p className="font-medium">{selectedEmail.from_address}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Recibido</p>
                  <p className="font-medium">{formatDate(selectedEmail.received_at)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-zinc-400">Asunto</p>
                  <p className="font-medium">{selectedEmail.subject}</p>
                </div>
              </div>

              {/* Status and Channel */}
              <div className="flex gap-2">
                <ChannelBadge channel={selectedEmail.detected_channel} />
                <Badge className={`${STATUS_CONFIG[selectedEmail.parse_status]?.color} text-white`}>
                  {STATUS_CONFIG[selectedEmail.parse_status]?.label}
                </Badge>
              </div>

              {/* Error Message */}
              {selectedEmail.error_message && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-sm">{selectedEmail.error_message}</p>
                  </div>
                </div>
              )}

              {/* Body */}
              <div>
                <p className="text-sm text-zinc-400 mb-2">Contenido</p>
                <pre className="bg-black/30 p-4 rounded-lg text-sm overflow-x-auto max-h-60 whitespace-pre-wrap">
                  {selectedEmail.body_plain || '(Sin contenido)'}
                </pre>
              </div>

              {/* Parsed Data Editor */}
              {editedData && (
                <div>
                  <p className="text-sm text-zinc-400 mb-2">Datos Extraídos (editable)</p>
                  <div className="grid grid-cols-2 gap-4 bg-black/30 p-4 rounded-lg">
                    <div>
                      <label className="text-xs text-zinc-400">ID Externo</label>
                      <Input
                        value={editedData.external_booking_id || ''}
                        onChange={(e) => setEditedData({...editedData, external_booking_id: e.target.value})}
                        className="input-gold mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Cliente</label>
                      <Input
                        value={editedData.customer_name || ''}
                        onChange={(e) => setEditedData({...editedData, customer_name: e.target.value})}
                        className="input-gold mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Email</label>
                      <Input
                        value={editedData.customer_email || ''}
                        onChange={(e) => setEditedData({...editedData, customer_email: e.target.value})}
                        className="input-gold mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Teléfono</label>
                      <Input
                        value={editedData.customer_phone || ''}
                        onChange={(e) => setEditedData({...editedData, customer_phone: e.target.value})}
                        className="input-gold mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Matrícula</label>
                      <Input
                        value={editedData.plate || ''}
                        onChange={(e) => setEditedData({...editedData, plate: e.target.value})}
                        className="input-gold mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Importe (€)</label>
                      <Input
                        type="number"
                        value={(editedData.amount_cents || 0) / 100}
                        onChange={(e) => setEditedData({...editedData, amount_cents: Math.round(parseFloat(e.target.value) * 100)})}
                        className="input-gold mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Entrada</label>
                      <Input
                        type="datetime-local"
                        value={editedData.start_dt ? editedData.start_dt.slice(0, 16) : ''}
                        onChange={(e) => setEditedData({...editedData, start_dt: e.target.value + ':00+01:00'})}
                        className="input-gold mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Salida</label>
                      <Input
                        type="datetime-local"
                        value={editedData.end_dt ? editedData.end_dt.slice(0, 16) : ''}
                        onChange={(e) => setEditedData({...editedData, end_dt: e.target.value + ':00+01:00'})}
                        className="input-gold mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button onClick={() => retryParse(selectedEmail.id)} className="btn-secondary">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reprocesar
                </Button>
                <Button onClick={createReservation} className="btn-primary">
                  <Check className="w-4 h-4 mr-2" />
                  {selectedEmail.linked_reservation_id ? 'Actualizar Reserva' : 'Crear Reserva'}
                </Button>
                <Button onClick={ignoreEmail} variant="destructive" className="ml-auto">
                  <X className="w-4 h-4 mr-2" />
                  Ignorar
                </Button>
              </div>

              {/* Linked Reservation */}
              {selectedEmail.linked_reservation_id && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-4 h-4" />
                      <p className="text-sm">Vinculado a reserva</p>
                    </div>
                    <Button
                      size="sm"
                      className="btn-secondary"
                      onClick={() => window.open(`/admin/reservas?id=${selectedEmail.linked_reservation_id}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Ver Reserva
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInbox;
